import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { MaintenanceSchedule } from '../lib/api';

interface MaintenanceReminder {
  id: string;
  title: string;
  daysUntil: number;
  nextDate: Date;
  category?: string;
  type: 'maintenance';
}

interface NotificationContextType {
  permission: NotificationPermission | 'default';
  isSupported: boolean;
  requestPermission: () => Promise<boolean>;
  showNotification: (title: string, options?: NotificationOptions) => void;
  scheduleReminder: (reminder: ReminderInput) => ReturnType<typeof setTimeout> | null;
  checkMaintenanceReminders: (schedules: MaintenanceSchedule[], dismissedReminders?: string[]) => MaintenanceReminder[];
  swRegistration: ServiceWorkerRegistration | null;
}

interface ReminderInput {
  id: string;
  title: string;
  message: string;
  scheduledTime: string | Date;
  url?: string;
}

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | 'default'>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Check browser support
  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          setSwRegistration(registration);
          console.log('Service Worker registered');
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Show local notification
  const showNotification = useCallback((title: string, options: NotificationOptions = {}) => {
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    const defaultOptions: NotificationOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'forvaltningen-notification',
      requireInteraction: false,
      ...options
    };

    // Use service worker if available
    if (swRegistration) {
      swRegistration.showNotification(title, defaultOptions);
    } else if ('Notification' in window) {
      new Notification(title, defaultOptions);
    }
  }, [permission, swRegistration]);

  // Schedule a reminder notification
  const scheduleReminder = useCallback((reminder: ReminderInput): ReturnType<typeof setTimeout> | null => {
    const { id, title, message, scheduledTime, url } = reminder;
    
    // Calculate time until notification
    const now = new Date();
    const notifyTime = new Date(scheduledTime);
    const delay = notifyTime.getTime() - now.getTime();
    
    if (delay <= 0) {
      // Time has passed, show immediately
      showNotification(title, { body: message, data: { url } });
      return null;
    }
    
    // Schedule the notification
    const timeoutId = setTimeout(() => {
      showNotification(title, { 
        body: message, 
        data: { url },
        tag: `reminder-${id}`
      });
    }, delay);
    
    return timeoutId;
  }, [showNotification]);

  // Check for maintenance reminders
  const checkMaintenanceReminders = useCallback((schedules: MaintenanceSchedule[], dismissedReminders: string[] = []): MaintenanceReminder[] => {
    if (permission !== 'granted') return [];
    
    const now = new Date();
    const reminderDays = 7; // 7 days before
    const reminders: MaintenanceReminder[] = [];
    
    for (const schedule of schedules) {
      // Skip if reminder is disabled or dismissed
      if (schedule.reminder_enabled === false) continue;
      if (dismissedReminders.includes(schedule.id)) continue;
      
      // Calculate next date
      let nextDateStr = schedule.next_date || schedule.due_date || schedule.start_date;
      if (!nextDateStr) continue;
      
      const nextDate = new Date(nextDateStr);
      const daysUntil = Math.ceil((nextDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      // Show reminder if within reminder window
      if (daysUntil >= 0 && daysUntil <= reminderDays) {
        reminders.push({
          id: schedule.id,
          title: schedule.title || 'Okänt underhåll',
          daysUntil,
          nextDate,
          category: schedule.category,
          type: 'maintenance'
        });
      }
    }
    
    return reminders.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [permission]);

  const value: NotificationContextType = {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    scheduleReminder,
    checkMaintenanceReminders,
    swRegistration
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
